import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Mail, Send } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Newsletter exit-intent popup.
 * Triggers when:
 *  - desktop: mouse leaves the viewport from the top
 *  - mobile / fallback: 45s after first visit OR on scroll past 70% of page
 * Persisted state (localStorage):
 *  - 'nl_popup_dismissed': set when user closes / submits  →  never show again for 30d
 */
const LS_KEY = "nl_popup_dismissed";
const TTL_DAYS = 30;
const SESSION_KEY = "nl_popup_shown_this_session";

function isDismissed() {
  try {
    // Once shown / dismissed in this session, never re-show until full reload
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < TTL_DAYS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(LS_KEY, String(Date.now()));
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}

function markShown() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const armed = useRef(false);
  const { pathname } = useLocation();

  // Don't distract paying users / admins
  const blockedPath =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/cart");

  useEffect(() => {
    if (blockedPath) return;
    if (isDismissed()) return;
    armed.current = true;

    const trigger = () => {
      if (!armed.current) return;
      armed.current = false;
      markShown();
      setOpen(true);
    };

    // 1) Desktop exit-intent: mouse leaves top of viewport
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) trigger();
    };

    // 2) Mobile fallback: scroll past 70%
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && scrolled / total > 0.7) trigger();
    };

    // 3) Time fallback: 45s after page load
    const timeout = setTimeout(trigger, 45000);

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timeout);
    };
  }, [blockedPath]);

  const close = () => {
    setOpen(false);
    markDismissed();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Email invalide");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Merci ! Votre code BIENVENUE10 arrive par email.");
      markDismissed();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-[#000B1A]/80 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={close}
      data-testid="newsletter-popup-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[#0A2A4F] border border-white/15 shadow-2xl"
        data-testid="newsletter-popup"
      >
        <button
          onClick={close}
          aria-label="Fermer"
          className="absolute top-3 right-3 p-2 text-white/60 hover:text-white hover:bg-white/10 transition"
          data-testid="newsletter-popup-close"
        >
          <X size={18} />
        </button>

        <div className="p-8 md:p-10">
          <div className="inline-flex items-center justify-center w-12 h-12 border border-white/20 mb-6">
            <Mail size={20} strokeWidth={1.25} className="text-[#C5CDD8]" />
          </div>

          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            Offre de bienvenue
          </div>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
            –10 % sur votre<br />
            <span className="text-[#C5CDD8]">première commande</span>
          </h2>

          <p className="mt-5 text-sm leading-relaxed text-white/70">
            Recevez un code de réduction immédiat + nos conseils ergonomie et
            les nouveautés du mois. Pas de spam, désinscription en 1 clic.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-3" data-testid="newsletter-popup-form">
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              className="w-full bg-[#001F3F] border border-white/20 text-white px-4 py-3 text-sm focus:outline-none focus:border-white"
              data-testid="newsletter-popup-email"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 disabled:opacity-50"
              data-testid="newsletter-popup-submit"
            >
              {submitting ? (
                "Envoi..."
              ) : (
                <>
                  Recevoir mon code –10 % <Send size={14} />
                </>
              )}
            </button>
          </form>

          <button
            onClick={close}
            className="mt-5 block w-full text-center text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70"
          >
            Non merci, je préfère payer plein tarif
          </button>
        </div>
      </div>
    </div>
  );
}
